import { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useBoolean, useCopyToClipboard } from 'react-use'

import { ProjectDomainAPI } from '@makyoapp/makyo-sdk'
import { ErrorResponse } from '@makyoapp/makyo-sdk/api/api'
import { ProjectStatus } from '@makyoapp/makyo-sdk/models/Projects'
import { isValidSubdomain } from '@makyoapp/makyo-common'
import { Clipboard16, EditDomain16, LinkAlt16, Trash16 } from '@makyoapp/makyo-icon/icon'
import { MaterialIcon } from '@makyoapp/makyo-tw-ui'
import { Badge, Button, ButtonColor, ButtonVariant, ColorVariant, Field, SimpleInput } from '@makyoapp/makyo-ui'

import { useReqDeleteCustomDomain } from '@/resolvers/project/mutation'
import { useSingleProjectContext } from '@/context/SingleProjectContext'
import { useReqError } from '@/hooks/useReqError'
import { MAKYO_APP_DOMAIN } from '@/shared/constants'
import { useDebounceInput } from '@/shared/makyo-hooks'
import { AccordionItem } from '@/components/Accordion/Accordion'

import DomainField from '../DomainField'
import { DnsConfigInstructionModal } from './DnsConfigInstructionModal'

function DomainAccordionItem() {
  const { t } = useTranslation()
  const [copyState, copyToClipboard] = useCopyToClipboard()

  const { project, updateProject } = useSingleProjectContext()
  const { mutate } = useReqDeleteCustomDomain()
  const printAPIError = useReqError()

  const [oldProjectAppUrl] = useState(project?.href || '')

  // Derived state
  const projectAppUrl = project?.href || ''
  const projectId = project?.id

  // const urlSuggestion = project?.title.replace(/\s/gi, '-') + '1'

  // App Url State
  const url = `https://${projectAppUrl}.${MAKYO_APP_DOMAIN}`
  const [debounceAppUrl, , , appUrl, setAppUrl] = useDebounceInput(projectAppUrl)
  const isDirty = projectAppUrl !== appUrl

  // Local State
  const [isAddDomain, setIsAddDomain] = useBoolean(false)
  const [error, setError] = useState('')
  const [isLoadingUpdateDomain, setLoadingUpdateDomain] = useState(false)
  const [isDelLoading, setIsDelLoading] = useState('')
  const [isVerifyLoading, setIsVerifyLoading] = useState('')
  const [showChars, setShowChars] = useState(false)
  const [showDnsConfig, setShowDnsConfig] = useState(false)

  // validate & update sub domain
  useEffect(() => {
    // Define an asynchronous function for the effect
    const updateDomain = async () => {
      if (debounceAppUrl.length < 2 || debounceAppUrl.length > 30) {
        return
      }

      // Only execute the effect if `debounceAppUrl` and `project` are defined, and `debounceAppUrl` is different from `project.href`
      if (!!projectId && debounceAppUrl !== projectAppUrl) {
        // only allow the slug to be alpha numeric characters, and remove the hypens at the start or end
        const newUrl = debounceAppUrl.replace(/[^a-zA-Z0-9-]/g, '').replace(/^-|-$/g, '')

        setAppUrl(newUrl)

        // Check if `debounceSlug` is a valid subdomain
        if (isValidSubdomain(newUrl) && newUrl !== projectAppUrl) {
          // Update Domain
          setLoadingUpdateDomain(true)

          ProjectDomainAPI.ReqUpdateMakyoDomain(projectId, {
            _MakyoDomain: newUrl,
          })
            .then(() => {
              // set Project data and slug
              updateProject((prev) => ({ ...prev, href: newUrl }))

              window.dispatchEvent(new CustomEvent('builder-domain-updated', { detail: { domain: newUrl } }))

              // Show Toast
              toast(t('Domain updated!'))
            })
            .catch((e) => {
              const err = e as unknown as ErrorResponse
              // If an error occurs, check if the error message indicates that the domain is already taken
              if (err?.message.toLowerCase().includes('taken')) {
                setError('Subdomain taken, please choose another.')
                return
              } else {
                printAPIError(e as unknown as ErrorResponse)
              }
            })
            .finally(() => {
              setLoadingUpdateDomain(false)
            })
        }
      }
    }

    // Execute the effect and handle any errors
    updateDomain()
  }, [t, debounceAppUrl, printAPIError, updateProject, setAppUrl, projectId, projectAppUrl])

  // Function to handle the copying of the slug to the clipboard
  const handleCopytoClipboard = () => {
    if (appUrl) {
      copyToClipboard(`${appUrl}.${MAKYO_APP_DOMAIN}`)
    }
    if (!copyState.error) {
      toast(t('Link copied to clipboard'))
    } else {
      toast(t('Failed to copy text to clipboard'))
    }
  }

  // Function to handle the deletion of a custom domain
  const handleDeleteCustomDomain = async (domain: { id: string; domain: string; verified: boolean }) => {
    if (project) {
      setIsDelLoading(domain.domain)
      mutate(
        { projectId: project.id, domainId: domain.id },
        {
          // On successful deletion, update the `project` state to remove the deleted domain
          onSuccess() {
            updateProject(() => ({
              ...project,
              domains: project.domains.filter((d) => d.domain !== domain.domain),
            }))
          },
          // On error deletion, print error
          onError(e) {
            printAPIError(e)
          },
          // On operation settled deletion, set state to empty string
          onSettled() {
            setIsDelLoading('')
          },
        }
      )
    }
  }

  // restore subdomain if its empty
  useEffect(() => {
    let timeout: NodeJS.Timeout

    if (isDirty) {
      if (!appUrl) {
        timeout = setTimeout(() => {
          setAppUrl(projectAppUrl)

          if (error) {
            setError('')
          }
        }, 4000)
      }
    }

    return () => {
      clearTimeout(timeout)
    }
  }, [appUrl, error, isDirty, projectAppUrl, setAppUrl, setError])

  return (
    <>
      <AccordionItem itemId='setting-domain' title={<Trans>App URL</Trans>}>
        <Field isVertical className='min-h-0!'>
          <div className='flex-c'>
            <div className='w-full min-w-0'>
              <SimpleInput
                className='min-w-0'
                inputClassName='min-w-0'
                value={appUrl}
                onChange={(e) => {
                  e.preventDefault()
                  setShowChars(true)
                  setError('')
                  setAppUrl(e.currentTarget.value.toLowerCase())
                  if (e.target.value.length < 2) {
                    setError('The subdomain must be at least 2 characters long')
                  }
                  if (e.target.value.length > 30) {
                    setError('Max characters exceeded')
                  }
                }}
                onBlur={() => {
                  setShowChars(false)
                }}
                loading={isLoadingUpdateDomain}
                showClear={!!appUrl}
                onClear={() => {
                  setAppUrl('')
                }}
                // after={
                //   isLoadingUpdateDomain ? (
                //     <span className='loading spin w-[1rem]! h-[1rem]!' />
                //   ) : (
                //     isDirty && (
                //       <CheckOrCross
                //         value={!error && isValidSubdomain(appUrl)}
                //       />
                //     )
                //   )
                // }
              />
            </div>
            <span className='text-light'>{MAKYO_APP_DOMAIN}</span>
            <Button
              variant={ButtonVariant.Ghost}
              disabled={!appUrl}
              noPadding={true}
              className='w-9 h-9'
              onClick={handleCopytoClipboard}
              icon={<Clipboard16 />}
            />
          </div>
        </Field>

        <div className='text-m-sm pl-0.5'>
          {!!error ? (
            <div className='red'>{error}</div>
          ) : (
            <div className='text-light'>
              {showChars && !!appUrl.trim().length
                ? `${30 - appUrl.trim().length} characters left`
                : 'Use 2-30 letters, numbers, or hyphen'}
            </div>
          )}
        </div>

        <div className='text-m-sm pb-2 mt-4'>
          {projectAppUrl !== oldProjectAppUrl ? (
            <div className='flex items-center gap-1.5 text-red-600'>
              <MaterialIcon icon='info' size={15} />
              <p>If you change the subdomain, you must re-publish your app for the new address to take effect.</p>
            </div>
          ) : project?.status === ProjectStatus.Draft ? (
            <div className='flex items-center gap-1.5 text-red-600'>
              <MaterialIcon icon='info' size={15} />
              <p>You must publish your app before it can be viewed in the browser.</p>
            </div>
          ) : project?.status === ProjectStatus.Published && projectAppUrl ? (
            <div className='mt-4 text-m-sm flex flex-wrap'>
              <div className='text-light mr-2'>
                <Trans>Your URL:</Trans>
              </div>
              <a className='link' href={url} target='_blank' rel='noreferrer'>
                {url}
              </a>
            </div>
          ) : (
            <div className='flex items-center gap-1.5 text-red-600'>
              <MaterialIcon icon='info' size={15} />
              <p>If you change the custom domain, you must re-publish your app for the change to be applied.</p>
            </div>
          )}
        </div>
      </AccordionItem>

      <AccordionItem itemId='custom-domain' title={<Trans>Custom Domain</Trans>}>
        <Field isVertical>
          <div>
            {/*
             <Info className='text-m-sm mb-4'>
             <Trans values={{ url }}>
             Your server is always available on{' '}
             <a className='link' href={url}>
             {url}
             </a>
             </Trans>
             </Info>
             */}
            <div className='text-light text-[12px] mb-3'>
              {/* <Trans>
                You can also point custom domains you own to this app. See{' '}
                <a href='#' className='link' onClick={() => setShowDnsConfig(true)}>
                  DNS configuration instructions
                </a>
                .
              </Trans> */}
              <a
                href='https://chatgpt.com/g/g-68a7f87c0c6481919a058b562f1624a0-makyo-dns-wizard'
                target='_blank'
                rel='noreferrer'
                className='link flex items-center gap-1.5'
              >
                <MaterialIcon icon='info' size={15} />{' '}
                <span className='hover:underline'>Step-by-Step DNS Setup Instructions</span>
              </a>
            </div>
            <div className='flex flex-col'>
              {project?.domains.map((domain, index) => {
                return (
                  <div
                    key={`domain-${index}`}
                    className='flex-c px-2 py-1 rounded-md hover:bg-zinc-200 dark:hover:bg-slate-700'
                  >
                    <div>
                      <EditDomain16 />
                    </div>
                    <div className='w-full min-w-0'>{domain.domain}</div>
                    <LinkAlt16
                      className='w-4 h-4 shrink-0 cursor-pointer'
                      onClick={() => {
                        window.open(`https://${domain.domain}`, '_blank')
                      }}
                    />
                    {!domain.verified ? (
                      <Button
                        icon={<MaterialIcon icon='⚠️' size={15} />}
                        className='h-7! !border-danger-600 !text-danger-500'
                        disabled={isVerifyLoading === domain.domain}
                        variant={ButtonVariant.Outlined}
                        onClick={async () => {
                          if (project) {
                            setIsVerifyLoading(domain.domain)
                            try {
                              await ProjectDomainAPI.ReqVerifyCustomDomain(project.id, domain.id)
                              const newDomains = [...project.domains]
                              newDomains[index].verified = true

                              updateProject({
                                ...project,
                                domains: newDomains,
                              })
                            } catch (e) {
                              printAPIError(e as ErrorResponse)
                            } finally {
                              setIsVerifyLoading('')
                            }
                          }
                        }}
                      >
                        {isVerifyLoading === domain.domain ? <span className='loading' /> : t('Verify')}
                      </Button>
                    ) : (
                      <Badge color={ColorVariant.Teal}>{t('Verified')}</Badge>
                    )}
                    <Button
                      className='shrink-0'
                      disabled={isDelLoading === domain.domain}
                      onClick={() => handleDeleteCustomDomain(domain)}
                      noPadding
                      squared
                      variant={ButtonVariant.Ghost}
                      icon={isDelLoading === domain.domain ? <span className='loading' /> : <Trash16 />}
                    />
                  </div>
                )
              })}
            </div>
            {isAddDomain && (
              <DomainField
                onSave={() => {
                  //const newDomains = [...domains, domain]
                  //setDomains(newDomains)
                  setIsAddDomain(false)
                }}
                onCancel={() => setIsAddDomain(false)}
              />
            )}
            <Button className='w-full mt-4' disabled={isAddDomain} onClick={() => setIsAddDomain(true)}>
              {t('Add Domain')}
            </Button>
          </div>
        </Field>
      </AccordionItem>

      <DnsConfigInstructionModal showDnsConfig={showDnsConfig} setShowDnsConfig={setShowDnsConfig} />
    </>
  )
}

export default DomainAccordionItem
